import React, { useState, useEffect } from 'react';
import { X, Building2, MapPin, Phone, Mail, User, Users, ArrowRight, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Branch } from '../../types';

interface BranchDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    branch: Branch;
    onViewEmployee: (employee: any) => void;
}

const BranchDetailModal: React.FC<BranchDetailModalProps> = ({ isOpen, onClose, branch, onViewEmployee }) => {
    const [employees, setEmployees] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'employees'>('overview');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedUnits, setExpandedUnits] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (isOpen && branch) {
            fetchEmployees();
        }
    }, [isOpen, branch]);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            // Fetch Profiles
            const { data: profiles, error: profileError } = await supabase
                .from('profiles')
                .select('*')
                .eq('branch_id', branch.id)
                .eq('is_active', true);

            if (profileError) throw profileError;

            // Fetch Units for this branch to map unit names
            const { data: units, error: unitError } = await supabase
                .from('units')
                .select('id, name, type')
                .eq('branch_id', branch.id);

            if (unitError) console.error('Error fetching units:', unitError);

            // Map units to profiles
            const mappedProfiles = (profiles || []).map((p: any) => ({
                ...p,
                unit_name: units?.find((u: any) => u.id === p.unit_id)?.name || 'Birim Atanmamış',
                unit_type: units?.find((u: any) => u.id === p.unit_id)?.type
            }));

            setEmployees(mappedProfiles);

            // Initialize all units as expanded
            const uniqueUnits = Array.from(new Set(mappedProfiles.map((p: any) => p.unit_name)));
            const initialExpanded = uniqueUnits.reduce((acc: any, unit: any) => ({ ...acc, [unit]: true }), {});
            setExpandedUnits(initialExpanded);

        } catch (error) {
            console.error('Error fetching employees:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleUnit = (unitName: string) => {
        setExpandedUnits(prev => ({ ...prev, [unitName]: !prev[unitName] }));
    };

    const filteredEmployees = employees.filter(emp =>
        (emp.first_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (emp.last_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (emp.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    // Group employees by unit
    const groupedEmployees: Record<string, any[]> = {};
    filteredEmployees.forEach(emp => {
        const unitName = emp.unit_name;
        if (!groupedEmployees[unitName]) {
            groupedEmployees[unitName] = [];
        }
        groupedEmployees[unitName].push(emp);
    });

    // Sort units alphabetically, but keep "Birim Atanmamış" at the end if exists
    const sortedUnits = Object.keys(groupedEmployees).sort((a, b) => {
        if (a === 'Birim Atanmamış') return 1;
        if (b === 'Birim Atanmamış') return -1;
        return a.localeCompare(b);
    });

    if (!isOpen || !branch) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex flex-col relative bg-gradient-to-r from-blue-600 to-purple-600 p-6 shrink-0">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="flex items-start space-x-4 mt-2">
                        <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20">
                            <Building2 className="w-8 h-8 text-white" />
                        </div>
                        <div className="text-white">
                            <h2 className="text-2xl font-bold">{branch.name}</h2>
                            <div className="flex items-center space-x-4 mt-2 text-blue-100">
                                <div className="flex items-center space-x-1">
                                    <MapPin className="w-4 h-4" />
                                    <span>{branch.city}, {branch.region}</span>
                                </div>
                                <span>•</span>
                                <div className="flex items-center space-x-1">
                                    <Users className="w-4 h-4" />
                                    <span>{employees.length} Çalışan</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex space-x-1 mt-6 border-b border-white/10">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'overview'
                                ? 'bg-white text-blue-600'
                                : 'text-blue-100 hover:bg-white/10'
                                }`}
                        >
                            Genel Bakış
                        </button>
                        <button
                            onClick={() => setActiveTab('employees')}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'employees'
                                ? 'bg-white text-blue-600'
                                : 'text-blue-100 hover:bg-white/10'
                                }`}
                        >
                            Çalışanlar
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900/50">
                    {activeTab === 'overview' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <Building2 className="w-5 h-5 mr-2 text-blue-500" />
                                    İletişim Bilgileri
                                </h3>
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                                        <User className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="text-xs text-gray-500">Yönetici</p>
                                            <p className="font-medium">{branch.contactPerson}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                                        <Phone className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="text-xs text-gray-500">Telefon</p>
                                            <p className="font-medium">{branch.phone}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                                        <Mail className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="text-xs text-gray-500">Email</p>
                                            <p className="font-medium">{branch.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                                        <MapPin className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="text-xs text-gray-500">Adres</p>
                                            <p className="font-medium">{branch.address}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <Users className="w-5 h-5 mr-2 text-green-500" />
                                    Performans Özeti
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                        <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Toplam Lead</p>
                                        <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{branch.totalStudents}</p>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">Bu Ay Kayıt</p>
                                        <p className="text-2xl font-bold text-green-700 dark:text-green-300">{branch.currentMonthRegistrations}</p>
                                    </div>
                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                        <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Aylık Hedef</p>
                                        <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{branch.monthlyTarget}</p>
                                    </div>
                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                                        <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Başarı Oranı</p>
                                        <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">
                                            %{Math.round((branch.currentMonthRegistrations / (branch.monthlyTarget || 1)) * 100)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col h-full">
                            {/* Search Bar */}
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Çalışan ara (isim, email...)"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Employee List */}
                            <div className="flex-1 overflow-y-auto">
                                {loading ? (
                                    <div className="flex justify-center items-center h-48">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : sortedUnits.length > 0 ? (
                                    <div className="space-y-4 p-4">
                                        {sortedUnits.map((unitName) => (
                                            <div key={unitName} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                                <button
                                                    onClick={() => toggleUnit(unitName)}
                                                    className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        {expandedUnits[unitName] ? (
                                                            <ChevronDown className="w-5 h-5 text-gray-500" />
                                                        ) : (
                                                            <ChevronRight className="w-5 h-5 text-gray-500" />
                                                        )}
                                                        <h4 className="font-semibold text-gray-900 dark:text-white">
                                                            {unitName}
                                                        </h4>
                                                        <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full">
                                                            {groupedEmployees[unitName].length}
                                                        </span>
                                                    </div>
                                                </button>

                                                {expandedUnits[unitName] && (
                                                    <div className="divide-y divide-gray-200 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700">
                                                        {groupedEmployees[unitName].map((emp) => (
                                                            <div key={emp.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-between group bg-white dark:bg-gray-900">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold text-sm">
                                                                        {emp.first_name?.[0]}{emp.last_name?.[0]}
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                                                            {emp.first_name} {emp.last_name}
                                                                        </h4>
                                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                            {emp.email}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => onViewEmployee(emp)}
                                                                    className="opacity-0 group-hover:opacity-100 flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700 font-medium transition-all"
                                                                >
                                                                    <span>Detay</span>
                                                                    <ArrowRight className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-8 text-center text-gray-500">
                                        <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                        <p>Çalışan bulunamadı.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BranchDetailModal;
